import type { ReactElement } from 'react'
import { useState } from 'react'
import { Card, CardHead } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import {
  DEFAULT_TIMEZONE,
  browserTimezone,
  momentIn,
  timezoneChoices,
  useSaveTimezone,
  useTimezone,
} from '~/state/timezone'

const CSS = `
.nw-timezone p { margin: 0 0 var(--nw-space-3); }
.nw-timezone .nw-decide { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-3); margin-top: var(--nw-space-4); }
.nw-timezone label > span { color: var(--nw-muted); display: block; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-timezone select, .nw-timezone input { min-width: 260px; }
.nw-timezone .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0; }
.nw-timezone .nw-unsaved { color: var(--nw-body-ink); font-size: var(--nw-text-chip); margin: 0; }
.nw-timezone .nw-suggest { align-items: center; color: var(--nw-muted); display: flex; flex-wrap: wrap; font-size: var(--nw-text-cell); gap: var(--nw-space-2); }
.nw-timezone .nw-note { color: var(--nw-muted); font-size: var(--nw-text-chip); max-width: 70ch; }
.nw-timezone .nw-ran { color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: var(--nw-space-4) 0 0; max-width: 70ch; }
`

/**
 * The zone this household's days are cut at.
 *
 * Ungated, beside the base currency: every household lives in a zone whether or
 * not it has connected anything, and there is no row to create before this can
 * be answered.
 *
 * The browser's zone is offered rather than adopted. It is where the reader is
 * sitting now, which is usually the household's and is not the same claim —
 * adopting it on sight would move the day a whole history is cut at because
 * somebody opened a page in a hotel.
 */
export function TimezonePanel(): ReactElement {
  const current = useTimezone()
  const save = useSaveTimezone()
  const [chosen, setChosen] = useState<string | null>(null)

  const stored = current.data?.value
  const shown = chosen ?? stored ?? DEFAULT_TIMEZONE
  const edited = stored !== undefined && shown !== stored
  const busy = current.isPending || save.isPending

  const zones = timezoneChoices()
  const browser = browserTimezone()
  // Read in the zone on screen rather than the one on file, so trying a zone
  // out shows what the last collection would have looked like under it. That is
  // the question somebody changing this control is asking.
  const lastAt = current.data?.lastCollectionAt ?? null
  const ranAt = lastAt === null ? undefined : momentIn(lastAt, shown)
  // Said only where it would change something. A reader whose browser agrees
  // with what is stored is being told the product is working, which is not news.
  const suggestion =
    browser !== undefined && current.data?.stored === false && browser !== stored ? browser : undefined

  return (
    <Card className="nw-timezone">
      <style>{CSS}</style>
      <CardHead
        id="nw-timezone-heading"
        title="The zone your days are cut at"
        lede="A snapshot is filed under a calendar day, and which day a moment falls on depends on where you are. The nightly run reads this to decide which day it is writing down."
      />

      {current.error && <p className="nw-error">Could not read the zone: {current.error.message}</p>}
      {save.error && <p className="nw-error">Could not save: {save.error.message}</p>}

      <div className="nw-decide">
        <label>
          <span>Time zone</span>
          {zones.length > 0 ? (
            <select
              value={shown}
              disabled={busy}
              aria-label="The time zone this household's days are cut at"
              onChange={(event) => setChosen(event.target.value)}
            >
              {/* A stored zone this runtime does not enumerate still has to be
                  selectable, or opening the card would silently re-point it at
                  whatever happened to sort first. */}
              {(zones.includes(shown) ? zones : [shown, ...zones]).map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={shown}
              disabled={busy}
              aria-label="The time zone this household's days are cut at"
              onChange={(event) => setChosen(event.target.value)}
            />
          )}
        </label>
        {current.data?.stored === false && <StatusPill tone="caution" label="Not chosen yet" />}
        <Button
          kind="primary"
          disabled={busy || !edited}
          onClick={() => save.mutate(shown, { onSuccess: () => setChosen(null) })}
        >
          {save.isPending ? 'Saving…' : 'Save time zone'}
        </Button>
        {suggestion !== undefined && !edited && (
          <span className="nw-suggest">
            {/* Whose answer this is, said rather than implied. A control reading
                "Use Asia/Dubai" looks like the product knows where the household
                lives; it knows where the browser is, which is the same thing
                until somebody opens the page from an airport. */}
            Your browser says <b>{suggestion}</b>.{' '}
            <Button disabled={busy} onClick={() => setChosen(suggestion)}>
              Use it
            </Button>
          </span>
        )}
        {edited && !save.isPending && (
          <p className="nw-unsaved">Not saved yet — nothing already stamped has moved.</p>
        )}
      </div>

      {/*
        When the night actually ran, in the zone above — which is the one thing
        on this card a household can act on and the only evidence that its
        collection happens in the middle of its own afternoon.

        The moment the run wrote down, never a schedule. The two runtimes keep
        different clocks on purpose — 03:00 Europe/Copenhagen on Node, 02:00 UTC
        on the Worker, the same hour in winter and an hour apart all summer — so
        a card quoting either would be wrong on the other for half the year. A
        run says when it began and both of them write it.
      */}
      {ranAt !== undefined && (
        <p className="nw-ran">
          Your last collection began at <b>{ranAt}</b>, read in {shown}. Collections run on this
          deployment's schedule rather than yours, so the hour is the same for every household and
          lands wherever it lands in each one's day.
        </p>
      )}

      {/*
        Two things a household would otherwise find out from a date that looks
        wrong: which zone it is being stamped in while it has said nothing, and
        that answering does not restate what is already filed.
      */}
      <p className="nw-note">
        {current.data?.stored === false
          ? `No zone has been set, so days are being cut at ${DEFAULT_TIMEZONE}. `
          : ''}
        Snapshots already taken keep the day they were filed under. Changing this decides which day
        the next one is written down as, and leaves every record already on file saying what it
        said.
      </p>
    </Card>
  )
}
